import { arc, type DefaultArcObject, type PieArcDatum, pie } from 'd3-shape';
import { forwardRef, Fragment, useMemo } from 'react';

import { container } from './PieChartPlot.sty.js';
import { useGraphSize } from '../../../core/components/chart-layout/hooks/useGraphSize.js';
import { useTooltipReducer } from '../../../core/components/overlay-tooltip/useTooltipReducer.js';
import {
  CHART_VARIANT,
  PAD_ANGLE,
  SUBPIXEL_SLICES_ID,
} from '../../constants.js';
import {
  useFormatter,
  useTruncationMode,
} from '../../context/FormatterContext.js';
import { useLabels } from '../../context/LabelsContext.js';
import {
  useHighlightedIdState,
  useVisibleSlicesState,
} from '../../hooks/store/selectors.js';
import {
  type ChartVariant,
  type InternalLabel,
  InternalSliceData,
} from '../../types/internal/pie-chart.js';
import { PieChartProps } from '../../types/pie-chart.config.js';
import { buildChartSizes } from '../../utils/build-chart-sizes.js';
import { buildLabel } from '../../utils/build-label.js';
import { buildSubpixelSliceMarker } from '../../utils/build-subpixel-slice-marker.js';
import { collision, isInBounds } from '../../utils/label-collisions.js';
import { InnerWrapper } from '../InnerWrapper/InnerWrapper.js';
import { PieChartLabel } from '../PieChartLabel.js';
import { PieChartSlice } from '../PieChartSlice.js';

type PieChartPlotProps = Omit<PieChartProps, 'data'> & {
  variant: ChartVariant;
};

const getValue = (pieData: InternalSliceData) => pieData.relativeValue;

const pieLayout = pie<InternalSliceData>()
  .value(getValue)
  .padAngle(PAD_ANGLE)
  .sort(null);

export const PieChartPlot = forwardRef<HTMLDivElement, PieChartPlotProps>(
  (props, forwardedRef) => {
    const { variant } = props;
    const dispatchReducer = useTooltipReducer();
    const visibleSlices = useVisibleSlicesState();
    const highlightedId = useHighlightedIdState();
    const { height, width } = useGraphSize();
    const { hidden: hiddenLabels, valueType } = useLabels();
    const formatter = useFormatter();
    const truncationMode = useTruncationMode();
    const { topCenter, radius, leftCenter, innerRadius } = buildChartSizes(
      height,
      width,
      variant,
      visibleSlices,
      !!hiddenLabels,
    );

    const visibleIds = visibleSlices.map((slice) => slice.id);

    const arcs = useMemo(() => pieLayout(visibleSlices), [visibleSlices]);

    const pathGenerator = useMemo(
      () =>
        arc<PieArcDatum<InternalSliceData>>()
          .innerRadius(innerRadius)
          .outerRadius(radius),
      [innerRadius, radius],
    );

    const donutOverlay = useMemo(
      () =>
        arc()
          .innerRadius(innerRadius)
          .outerRadius(radius)
          .startAngle(0)
          .endAngle(360)({} as DefaultArcObject),
      [innerRadius, radius],
    ) as string | undefined;

    const sliceRenderData = useMemo(() => {
      let previousLabel: InternalLabel | undefined = undefined;

      return arcs.map((pieArc) => {
        const label = buildLabel(
          pieArc,
          pathGenerator,
          valueType,
          formatter,
          truncationMode,
          leftCenter,
        );

        const slice = pieArc.data;
        const isHighlighted = !highlightedId || highlightedId === slice.id;
        const showLabel =
          !hiddenLabels &&
          isInBounds(label, leftCenter, topCenter) &&
          (!previousLabel || !collision(label, previousLabel)) &&
          slice.id !== SUBPIXEL_SLICES_ID;

        if (showLabel) {
          previousLabel = label;
        }

        return { pieArc, label, isHighlighted, showLabel };
      });
    }, [
      arcs,
      pathGenerator,
      valueType,
      formatter,
      truncationMode,
      leftCenter,
      highlightedId,
      hiddenLabels,
      topCenter,
    ]);

    const subpixelSliceMarker = useMemo(() => {
      if (!highlightedId) {
        return undefined;
      }
      const bucketArc = arcs.find(
        (pieArc) => pieArc.data.id === SUBPIXEL_SLICES_ID,
      );
      return bucketArc
        ? buildSubpixelSliceMarker(
            bucketArc,
            highlightedId,
            innerRadius,
            radius,
          )
        : undefined;
    }, [arcs, highlightedId, innerRadius, radius]);

    return (
      <div className={container} ref={forwardedRef}>
        {variant === CHART_VARIANT.Donut && (
          <InnerWrapper
            width={width}
            height={height}
            innerRadius={innerRadius}
            visibleIds={visibleIds}
          />
        )}

        <svg width={width} height={height} style={{ overflow: 'visible' }}>
          <g
            transform={
              topCenter !== 0 || leftCenter !== 0
                ? `translate(${leftCenter}, ${topCenter})`
                : undefined
            }
            onMouseLeave={dispatchReducer({ type: 'SET_OUT_BOUNDS' })}
          >
            {variant === CHART_VARIANT.Pie ? (
              <circle
                cx="0"
                cy="0"
                r={Math.max(radius, 0)}
                fill="transparent"
              />
            ) : (
              <path d={donutOverlay} fill="transparent" />
            )}

            {sliceRenderData.map(
              ({ pieArc, label, isHighlighted, showLabel }) => (
                <Fragment key={`slice-${pieArc.data.id}`}>
                  <PieChartSlice
                    sliceId={`slice-${pieArc.data.id}`}
                    arc={pieArc}
                    data={pieArc.data}
                    path={pathGenerator}
                    isHighlighted={isHighlighted}
                  />
                  {showLabel && (
                    <PieChartLabel
                      x={label.textX}
                      y={label.textY}
                      text={label.text}
                      isHighlighted={isHighlighted}
                    />
                  )}
                </Fragment>
              ),
            )}
            {subpixelSliceMarker && (
              <path
                d={subpixelSliceMarker.path}
                fill={subpixelSliceMarker.color}
              />
            )}
          </g>
        </svg>
      </div>
    );
  },
);
